import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteIcon from "~/common/mui/icons/Delete";
import {
  Alert,
  type AlertColor,
  Box,
  Card,
  IconButton,
  Input,
  LinearProgress,
  Stack,
  styled,
  type SxProps,
  type Theme,
  Typography,
} from "~/common/mui";
import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";

export type FileUploadSelectionSource = "picker" | "drop";

export interface FileUploadSelection {
  files: File[];
  source: FileUploadSelectionSource;
  containsDirectory: boolean;
}

export interface FileUploadAreaProps {
  accept?: string;
  activeDropLabel: ReactNode;
  className?: string;
  disabled?: boolean;
  disabledText?: ReactNode;
  dropLabel: ReactNode;
  fileInputLabel: string;
  formatText?: ReactNode;
  onFilesSelected: (selection: FileUploadSelection) => void;
  selectLabel: ReactNode;
  sx?: SxProps<Theme>;
}

export interface FileUploadAttachment {
  fileName: string;
  previewUrl: string;
  sizeText: string;
  uploadedAtText: string;
}

export type FileUploadPendingOperation =
  | { type: "upload"; fileName: string }
  | { type: "delete" }
  | { type: "reconcile" };

export interface FileUploadFeedback {
  message: ReactNode;
  severity: AlertColor;
}

export interface FileUploadSummaryViewModel {
  confirmedAttachment: FileUploadAttachment | null;
  feedback?: FileUploadFeedback | null;
  pendingOperation: FileUploadPendingOperation | null;
}

export interface FileUploadSummaryLabels {
  cancelUpload: string;
  deleteAttachment: string;
  deleting: string;
  dismissFeedback: string;
  reconciling: string;
  uploading: string;
}

export interface FileUploadSummaryProps {
  className?: string;
  labels: FileUploadSummaryLabels;
  onCancelUpload?: () => void;
  onDelete: () => void;
  onDismissFeedback?: () => void;
  onPreview?: () => void;
  sx?: SxProps<Theme>;
  viewModel: FileUploadSummaryViewModel;
}

export interface FileUploadProps {
  children: ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
}

function isFileDrag(event: DragEvent<HTMLElement>) {
  return (
    event.dataTransfer.types.includes("Files") ||
    Array.from(event.dataTransfer.items).some((item) => item.kind === "file")
  );
}

function isDirectoryItem(item: DataTransferItem) {
  const candidate = item as DataTransferItem & {
    getAsEntry?: () => FileSystemEntry | null;
    webkitGetAsEntry?: () => FileSystemEntry | null;
  };
  const entry = candidate.getAsEntry?.() ?? candidate.webkitGetAsEntry?.();
  return entry?.isDirectory === true;
}

function hasDirectory(items: DataTransferItemList) {
  return Array.from(items).some(isDirectoryItem);
}

function FileUploadRoot({ children, className, sx }: FileUploadProps) {
  return (
    <Root className={className} sx={sx}>
      {children}
    </Root>
  );
}

function FileUploadArea({
  accept,
  activeDropLabel,
  className,
  disabled = false,
  disabledText,
  dropLabel,
  fileInputLabel,
  formatText,
  onFilesSelected,
  selectLabel,
  sx,
}: FileUploadAreaProps) {
  const disabledDescriptionId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const openPicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || (event.key !== "Enter" && event.key !== " ")) {
      return;
    }
    event.preventDefault();
    openPicker();
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) {
      return;
    }
    openPicker();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onFilesSelected({
      containsDirectory: false,
      files: Array.from(event.currentTarget.files ?? []),
      source: "picker",
    });
    event.currentTarget.value = "";
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled && isFileDrag(event)) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled && isFileDrag(event)) {
      event.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled || !isFileDrag(event)) {
      return;
    }
    onFilesSelected({
      containsDirectory: hasDirectory(event.dataTransfer.items),
      files: Array.from(event.dataTransfer.files),
      source: "drop",
    });
  };

  return (
    <AreaContainer className={className} sx={sx}>
      <Input
        inputRef={inputRef}
        type="file"
        disabled={disabled}
        inputProps={{ accept, "aria-label": fileInputLabel }}
        onChange={handleInputChange}
        sx={{ display: "none" }}
      />
      <UploadCard
        aria-disabled={disabled}
        aria-describedby={disabled && disabledText ? disabledDescriptionId : undefined}
        aria-label={fileInputLabel}
        data-dragging={isDragging ? "true" : "false"}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        <UploadContent>
          <CloudIcon aria-hidden />
          <Stack gap={1}>
            <Typography id={disabled && disabledText ? disabledDescriptionId : undefined} variant="labelLarge">
              {disabled && disabledText ? (
                disabledText
              ) : isDragging ? (
                activeDropLabel
              ) : (
                <>
                  <Box component="span" sx={{ color: "primary.main", textDecoration: "underline" }}>
                    {selectLabel}
                  </Box>{" "}
                  {dropLabel}
                </>
              )}
            </Typography>
            {!disabled && !isDragging && formatText && <Typography variant="bodySmall">{formatText}</Typography>}
          </Stack>
        </UploadContent>
      </UploadCard>
    </AreaContainer>
  );
}

function FileUploadSummary({
  className,
  labels,
  onCancelUpload,
  onDelete,
  onDismissFeedback,
  onPreview,
  sx,
  viewModel,
}: FileUploadSummaryProps) {
  const { confirmedAttachment, feedback, pendingOperation } = viewModel;
  const isPendingDelete = pendingOperation?.type === "delete";
  const isPendingUpload = pendingOperation?.type === "upload";
  const isPendingReconcile = pendingOperation?.type === "reconcile";

  return (
    <SummaryRoot aria-live="polite" className={className} sx={sx}>
      {isPendingUpload && (
        <PendingBlock>
          <PendingRow>
            <Typography variant="bodyMedium" noWrap>
              {pendingOperation.fileName}
            </Typography>
            {onCancelUpload && (
              <TextAction type="button" onClick={onCancelUpload}>
                {labels.cancelUpload}
              </TextAction>
            )}
          </PendingRow>
          <LinearProgress aria-label={labels.uploading} />
        </PendingBlock>
      )}

      {confirmedAttachment && (
        <AttachmentRow>
          <Stack minWidth={0} gap={0.5}>
            <AttachmentLink
              href={confirmedAttachment.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onPreview}
            >
              {confirmedAttachment.fileName}
            </AttachmentLink>
            <Typography variant="bodySmall" color="text.secondary">
              {confirmedAttachment.sizeText} · {confirmedAttachment.uploadedAtText}
            </Typography>
          </Stack>
          <IconButton
            aria-label={labels.deleteAttachment}
            disabled={Boolean(pendingOperation)}
            onClick={onDelete}
            size="small"
          >
            <DeleteIcon aria-hidden />
          </IconButton>
        </AttachmentRow>
      )}

      {(isPendingDelete || isPendingReconcile) && (
        <PendingBlock>
          <Typography variant="bodyMedium">{isPendingDelete ? labels.deleting : labels.reconciling}</Typography>
          <LinearProgress aria-label={isPendingDelete ? labels.deleting : labels.reconciling} />
        </PendingBlock>
      )}

      {feedback && (
        <SummaryAlert
          action={
            onDismissFeedback && (
              <TextAction type="button" onClick={onDismissFeedback}>
                {labels.dismissFeedback}
              </TextAction>
            )
          }
          severity={feedback.severity}
        >
          {feedback.message}
        </SummaryAlert>
      )}
    </SummaryRoot>
  );
}

const Root = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  width: "100%",
}));

const AreaContainer = styled(Box)({
  width: "100%",
});

const UploadCard = styled(Card)(({ theme }) => ({
  alignItems: "center",
  alignSelf: "stretch",
  backgroundColor: theme.palette.surfaceContainer.high,
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.625),
  cursor: "pointer",
  color: theme.palette.text.label,
  display: "flex",
  justifyContent: "center",
  minHeight: theme.spacing(17),
  outline: "none",
  padding: theme.spacing(4, 0),
  transition: "background-color 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.surfaceContainer.highest,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: theme.spacing(0.25),
  },
  '&[aria-disabled="true"]': {
    cursor: "not-allowed",
    opacity: 0.6,
    "&:hover": {
      backgroundColor: theme.palette.surfaceContainer.high,
    },
  },
  '&[data-dragging="true"]': {
    backgroundColor: theme.palette.primary.container,
    borderColor: theme.palette.primary.main,
    borderStyle: "solid",
    borderWidth: 2,
    color: theme.palette.primary.onContainer,
    "& span": {
      color: "inherit",
    },
  },
}));

const UploadContent = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(3),
  justifyContent: "center",
  padding: theme.spacing(0, 2),
  textAlign: "center",
}));

const CloudIcon = styled(CloudUploadOutlinedIcon)(({ theme }) => ({
  color: "inherit",
  fontSize: theme.spacing(9),
}));

const SummaryRoot = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  width: "100%",
}));

const AttachmentRow = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.surfaceContainer.low,
  borderRadius: theme.spacing(1),
  flexDirection: "row",
  gap: theme.spacing(2),
  justifyContent: "space-between",
  padding: theme.spacing(1, 1.5),
}));

const PendingRow = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "row",
  gap: theme.spacing(2),
  justifyContent: "space-between",
  minHeight: theme.spacing(5),
}));

const PendingBlock = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  width: "100%",
}));

const SummaryAlert = styled(Alert)({
  width: "100%",
});

const AttachmentLink = styled("a")(({ theme }) => ({
  ...theme.typography.bodyMedium,
  color: theme.palette.primary.main,
  fontWeight: theme.typography.fontWeightBold,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const TextAction = styled("button")(({ theme }) => ({
  appearance: "none",
  background: "none",
  border: 0,
  color: theme.palette.primary.main,
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  textDecoration: "underline",
  whiteSpace: "nowrap",
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.38,
  },
}));

export const FileUpload = Object.assign(FileUploadRoot, {
  Area: FileUploadArea,
  Summary: FileUploadSummary,
});
